import { useEffect, useState } from "react";
import dayjs from "dayjs";
import Button from "../../componentes/button/button";
import { CustomTable } from "../../componentes/table/CustomTable";
import PagamentoCompensadaService from "../../services/resource/pagamentoCompensadaService";
import CompensadasCalendar from "./CompensadasCalendar";
import ImportarCompensadasModal from "./ImportarCompensadasModal";
import ConfigurarColunasModal from "./ConfigurarColunasModal";
import ConfrontoSapFortesPanel from "./ConfrontoSapFortesPanel";
import PagamentoCompensadaDashboardPanel from "./PagamentoCompensadaDashboardPanel";

const service = new PagamentoCompensadaService();

const colunasDetalhe = [
    { key: "filialNome", label: "Filial" },
    { key: "numeroDocumento", label: "Nº Documento" },
    { key: "divisao", label: "Divisão" },
    { key: "valor", label: "Valor", format: "currency", center: true },
    { key: "texto", label: "Texto" },
    { key: "referencia", label: "Referência" },
    { key: "dataDocumento", label: "Dt. Documento", format: "date" },
    { key: "dataPagamento", label: "Dt. Pagamento", format: "date" },
    { key: "dataCompensacao", label: "Dt. Compensação", format: "date" },
    { key: "codigoFornecedor", label: "Cód. Fornecedor" },
];

function PagamentoCompensadaPage() {
    document.title = "MyDash | Pagamento de Compensadas";

    const [showImportModal, setShowImportModal] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [dayDetail, setDayDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [activeTab, setActiveTab] = useState("calendario");
    const [confrontoPeriodoPreset, setConfrontoPeriodoPreset] = useState(null);

    const verConfrontoDoDia = () => {
        setConfrontoPeriodoPreset({
            dataEmissaoInicial: dayjs(selectedDate).subtract(90, "day").format("YYYY-MM-DD"),
            dataEmissaoFinal: selectedDate,
            dataCompensacaoEspecifica: selectedDate,
        });
        setActiveTab("confronto");
    };

    useEffect(() => {
        if (selectedDate) {
            buscarDetalheDia(selectedDate);
        }
    }, [selectedDate, refreshKey]);

    const buscarDetalheDia = async (data) => {
        setLoadingDetail(true);
        try {
            const res = await service.detalheDia(data);
            setDayDetail(res.data);
        } finally {
            setLoadingDetail(false);
        }
    };

    const formatarDataTitulo = (data) => {
        if (!data) return "";
        return new Date(data + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
    };

    return (
        <div className="min-h-screen bg-background p-4 md:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-xl font-bold font-display text-foreground">Pagamento de Compensadas</h1>
                    <p className="text-sm text-muted-foreground">
                        Importação e acompanhamento dos pagamentos compensados por data de compensação.
                    </p>
                </div>

                <div className="flex gap-2">
                    <Button
                        label="Configurar Colunas"
                        iconLeft="fa fa-sliders-h"
                        variant="white"
                        onClick={() => setShowConfigModal(true)}
                    />
                    <Button
                        label="Importar Excel"
                        iconLeft="fa fa-file-excel"
                        variant="indigo"
                        onClick={() => setShowImportModal(true)}
                    />
                </div>
            </div>

            <div className="flex gap-2 mb-5 border-b border-border">
                {[
                    { key: "calendario", label: "Calendário" },
                    { key: "confronto", label: "Confronto SAP × Fortes" },
                    { key: "dashboards", label: "Dashboards" },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === tab.key
                            ? "border-primary text-primary"
                            : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === "calendario" && (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            <CompensadasCalendar
                                service={service}
                                selectedDate={selectedDate}
                                onSelectDay={setSelectedDate}
                                refreshKey={refreshKey}
                            />
                        </div>

                        <div className="rounded-xl border border-border bg-card p-5 shadow-card">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-semibold text-foreground">
                                    {selectedDate ? formatarDataTitulo(selectedDate) : "Selecione um dia no calendário"}
                                </p>
                                {selectedDate && (
                                    <button
                                        onClick={verConfrontoDoDia}
                                        className="text-xs text-primary hover:underline whitespace-nowrap"
                                    >
                                        Ver confronto deste dia
                                    </button>
                                )}
                            </div>

                            {selectedDate && dayDetail && (
                                <div className="grid grid-cols-2 gap-3">
                                    <ResumoItem label="Registros" valor={dayDetail.quantidadeRegistros} />
                                    <ResumoItem label="Valor compensado" valor={(dayDetail.valorTotal ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} />
                                    <ResumoItem label="Fornecedores" valor={dayDetail.quantidadeFornecedores} />
                                    <ResumoItem label="Filiais" valor={dayDetail.quantidadeFiliais} />
                                </div>
                            )}

                            {selectedDate && !dayDetail && loadingDetail && (
                                <p className="text-sm text-muted-foreground">Carregando...</p>
                            )}
                        </div>
                    </div>

                    {selectedDate && (
                        <div className="mt-6">
                            <p className="text-sm font-semibold text-foreground mb-2">Registros do dia</p>
                            <CustomTable
                                data={dayDetail?.registros || []}
                                columns={colunasDetalhe}
                                loading={loadingDetail}
                                pagination
                                showHeader={false}
                                className="bg-white border shadow-card rounded-xl text-[11px]"
                                pageSize={20}
                            />
                        </div>
                    )}
                </>
            )}

            {activeTab === "confronto" && (
                <ConfrontoSapFortesPanel service={service} periodoPreset={confrontoPeriodoPreset} />
            )}

            {activeTab === "dashboards" && (
                <PagamentoCompensadaDashboardPanel service={service} />
            )}

            <ImportarCompensadasModal
                visible={showImportModal}
                onHide={() => setShowImportModal(false)}
                service={service}
                onImported={() => setRefreshKey(k => k + 1)}
            />

            <ConfigurarColunasModal
                visible={showConfigModal}
                onHide={() => setShowConfigModal(false)}
                service={service}
            />
        </div>
    );
}

function ResumoItem({ label, valor }) {
    return (
        <div className="rounded-lg border border-border bg-muted/40 p-3">
            <p className="text-base font-bold text-foreground">{valor}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
        </div>
    );
}

export default PagamentoCompensadaPage;
